'use client';

import { ReactNode, useEffect, useRef } from 'react';

import { checkIntersectionObserverHealth } from '@/hooks/use-intersection-observer-health';

type Props = { children: ReactNode; className?: string };

// px/s, as in the prototype.
const SPEED = 26;

const isHorizontal = (track: HTMLElement) => getComputedStyle(track).animationName.includes('-x');

// Each track's loop lasts its first set's length / SPEED (the CSS falls back to a per-card guess).
const setDurations = (box: HTMLElement) => {
  box.querySelectorAll<HTMLElement>('[data-pain-track]').forEach((track) => {
    const set = track.firstElementChild as HTMLElement | null;
    if (!set) return;
    const length = isHorizontal(track) ? set.offsetWidth : set.offsetHeight;
    if (length) track.style.setProperty('--pain-dur', `${(length / SPEED).toFixed(1)}s`);
  });
};

// Cascade order of the cards in view, in the direction their column moves: the "up" column from
// its lowest card, the "down" one from its highest; the phone rows from the side they come from.
const rankVisibleCards = (box: HTMLElement) => {
  box.querySelectorAll<HTMLElement>('[data-pain-track]').forEach((track) => {
    const column = track.parentElement;
    if (!column) return;
    const bounds = column.getBoundingClientRect();
    const horizontal = isHorizontal(track);
    const up = column.dataset.dir === 'up';
    const cards = Array.from(track.querySelectorAll<HTMLElement>('li'));
    cards.forEach((card) => card.style.setProperty('--pi', '0'));
    cards
      .map((card) => ({ card, rect: card.getBoundingClientRect() }))
      .filter(({ rect }) =>
        horizontal
          ? rect.right > bounds.left && rect.left < bounds.right
          : rect.bottom > bounds.top && rect.top < bounds.bottom,
      )
      .sort((a, b) =>
        horizontal
          ? up
            ? b.rect.left - a.rect.left
            : a.rect.left - b.rect.left
          : up
          ? b.rect.top - a.rect.top
          : a.rect.top - b.rect.top,
      )
      .forEach(({ card }, index) => card.style.setProperty('--pi', String(index)));
  });
};

/**
 * The pain-point columns of SliderWithGlassItems: sizes each loop to its content and, the first
 * time the section's heading comes into view, ranks the visible cards and sets `data-in`, which
 * plays their cascade (CSS). No IntersectionObserver, or one that never delivers: everything is
 * shown at once.
 */
export const PainColumns = ({ children, className }: Props) => {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const box = ref.current;
    if (!box) return undefined;
    const resize = () => setDurations(box);
    resize();
    window.addEventListener('resize', resize);
    document.fonts?.ready.then(resize);

    const show = () => {
      rankVisibleCards(box);
      box.setAttribute('data-in', '');
    };
    if (typeof IntersectionObserver === 'undefined') {
      show();
      return () => window.removeEventListener('resize', resize);
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        show();
        observer.disconnect();
      },
      { threshold: 0.15, rootMargin: '0px 0px -8% 0px' },
    );
    // The cascade follows the section's heading in (the prototype's reveal steps), not the columns.
    observer.observe(box.closest('section')?.querySelector('h2') ?? box);

    let isActive = true;
    checkIntersectionObserverHealth().then((healthy) => {
      if (isActive && !healthy) show();
    });
    return () => {
      isActive = false;
      observer.disconnect();
      window.removeEventListener('resize', resize);
    };
  }, []);

  return (
    <div ref={ref} className={className} data-pain-cols="">
      {children}
    </div>
  );
};
